import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ListCategoriaExamen,
    ListTipo,
    ProcedimientoActualizar,
    ProcedimientoNuevo,
} from "../../../Service/Service.categoriaExamen";
import { EliminarProcedimientoPrecio } from "../../../Service/Service.examen";
import { getCodeContifico } from "../../../Service/Service.ContificoAPI";
import { ControllerListarSegurosClinica } from "../../../Service/Services.seguros";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import Modal from "../../UI/Modal/Modal";
import Boton from "../../UI/Boton";
import { Campo, CampoTexto, CampoSelect } from "../../UI/Campo";
import Aviso, { useToast } from "../../UI/Aviso";
import {
    IconoBuscar,
    IconoCheck,
    IconoAlerta,
    IconoCopiar,
    IconoFlecha,
    IconoEliminar,
    IconoChevronIzquierda,
} from "../../UI/Iconos";
import { Avatar } from "./SelectorEntidad";
import { tituloClinico } from "../../../utils/textoClinico";
import {
    ALCANCE_CATALOGO,
    ALCANCE_PRECIO,
    camposCambiados,
    estaSincronizado,
    formatearPorcentaje,
    formatearPrecio,
    tocaElCatalogo,
    variacionPrecio,
} from "../../../utils/tarifarios";

/* ============================================================================
   TARIFARIOS-UI FASES 3 y 4 · modal de procedimiento

   Un solo componente para crear y editar, como antes. Lo que cambió:

   · El modal SABE de qué tarifario se trata. Antes "Entidad / Seguro" salía en
     "Seleccione", sin relación con la entidad que se estaba viendo, y crear un
     precio en el tarifario equivocado costaba un clic de despiste. Ahora la
     cabecera lo dice y el campo llega prellenado.
   · Las instrucciones eran el placeholder ("Hola, si tiene código contifico
     digite y luego buscar"), que desaparece justo al empezar a escribir.
     Ahora son texto de ayuda fijo.
   · "Buscar" no daba NINGUNA señal. Ahora el resultado se muestra siempre:
     encontrado, no encontrado, sin permiso o error de conexión.
   · Descripción era un `<textarea>` de dos filas para un nombre de una línea.
   · Al editar se ve el cambio de precio y a qué afecta (los dos avisos salen
     de utils/tarifarios.js, verificados contra el backend).
   · Eliminar está en el pie, no escondido en el menú de la fila, y su
     confirmación es ESTE MISMO modal cambiando de contenido -- nunca un
     diálogo encima de otro.

   LO QUE NO CAMBIÓ: los endpoints y la FORMA EXACTA del cuerpo que se les
   envía. `construirPayload` reproduce el objeto que el reducer anterior
   mandaba, campo por campo, incluido `estado: "A"` y el anidado
   `procedimiento_company_precios: { precio, descuento, id }`.
   ========================================================================== */

const ESTADO_INICIAL = {
    codigo: "",
    descripcion: "",
    categoria_id: "",
    tipo: "",
    seguro_id: "",
    precio: "",
};

/* El cuerpo que espera el backend. Se construye aquí, en un solo sitio, para
   que el formulario pueda tener la forma que le convenga sin arrastrar la del
   payload -- y para que cualquier cambio de contrato sea UNA línea. */
function construirPayload(form, { contificoId, descuento, idPrecio, estado }) {
    return {
        codigo: form.codigo,
        descripcion: form.descripcion,
        estado: estado || "A",
        tipo: form.tipo,
        categoria_id: Number(form.categoria_id) || 0,
        procedimiento_company_precios: {
            precio: Number(form.precio) || 0,
            descuento: Number(descuento) || 0,
            id: idPrecio || 0,
        },
        seguro_id: Number(form.seguro_id) || 0,
        contifico_id: contificoId ?? null,
    };
}

/* -- resultado de la búsqueda en Contífico --------------------------------
   Se modela como un dato, no como tres banderas sueltas, porque la pantalla
   tiene que poder decir SIEMPRE en cuál de los cuatro casos está. El código
   anterior no distinguía "no existe" de "Contífico está caído": los dos
   caían en el mismo `catch` y decían "Producto no verificado".
   ------------------------------------------------------------------------ */
const RESULTADO = {
    encontrado: (nombre, precio, id) => ({ tipo: "encontrado", nombre, precio, id }),
    noEncontrado: () => ({ tipo: "no-encontrado" }),
    sinPermiso: () => ({ tipo: "sin-permiso" }),
    error: () => ({ tipo: "error" }),
};

const ModalProcedimientoPrecio = ({
    show,
    onHide,
    successRefresh,
    procedimiento,
    entidades,
    entidadActual,
    categorias: categoriasProp,
    puedeEliminar = false,
}) => {
    const esEdicion = Boolean(procedimiento);
    const { mostrar } = useToast();

    const [paso, setPaso] = useState("formulario"); // "formulario" | "eliminar"
    const [form, setForm] = useState(ESTADO_INICIAL);
    const [original, setOriginal] = useState(ESTADO_INICIAL);
    const [contificoId, setContificoId] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [borrando, setBorrando] = useState(false);
    const [buscando, setBuscando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [tocado, setTocado] = useState({});
    const [copiado, setCopiado] = useState(false);

    // Catálogos. Categorías y entidades llegan por props desde la pantalla
    // (que ya las tenía) y sólo se piden aquí si no llegaran -- así el modal
    // sigue funcionando suelto sin duplicar peticiones cuando no hace falta.
    const [categoriasLocales, setCategoriasLocales] = useState([]);
    const [entidadesLocales, setEntidadesLocales] = useState([]);
    const [tipos, setTipos] = useState([]);

    const categorias = categoriasProp?.length ? categoriasProp : categoriasLocales;
    const listaEntidades = entidades?.length ? entidades : entidadesLocales;

    const fetchCatalogos = useCallback(async () => {
        // Los tipos son una lista cerrada de 11 valores en el backend
        // (FTipoExamen.ts). Se piden igual que antes.
        const tiposResp = await ListTipo();
        if (tiposResp?.data) {
            setTipos(tiposResp.data.map((t) => ({ value: t.tipo, label: tituloClinico(t.tipo) })));
        }
        if (!categoriasProp?.length) {
            const cats = await ListCategoriaExamen();
            if (cats?.data) {
                setCategoriasLocales(
                    cats.data.map((c) => ({
                        value: c.IdCategoriaExamen,
                        label: tituloClinico(c.Descripcion),
                    }))
                );
            }
        }
        if (!entidades?.length) {
            const segs = await ControllerListarSegurosClinica(GetTokenDecoded().idclinica);
            setEntidadesLocales((segs || []).map((s) => ({ value: s.idseguro, label: s.nombre })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoriasProp?.length, entidades?.length]);

    useEffect(() => {
        if (show) fetchCatalogos();
    }, [show, fetchCatalogos]);

    /* Cada apertura reconstruye el formulario desde cero. `original` guarda la
       foto de partida: es contra ella, y no contra el estado anterior del
       componente, como se decide si algo cambió. */
    useEffect(() => {
        if (!show) return;
        setPaso("formulario");
        setResultado(null);
        setTocado({});
        setCopiado(false);

        if (procedimiento) {
            const inicial = {
                codigo: procedimiento.ExamenTipo?.codigo || "",
                descripcion: procedimiento.ExamenTipo?.descripcion || "",
                categoria_id: procedimiento.ExamenTipo?.categoria_id ?? "",
                tipo: procedimiento.ExamenTipo?.tipo || "",
                seguro_id: procedimiento.seguro_id ?? "",
                precio: procedimiento.precio ?? "",
            };
            setForm(inicial);
            setOriginal(inicial);
            setContificoId(procedimiento.ExamenTipo?.contifico_id || null);
        } else {
            // FASE 3: la entidad llega PRELLENADA con el tarifario que se está
            // viendo. Es el arreglo más barato del error más caro.
            const inicial = { ...ESTADO_INICIAL, seguro_id: entidadActual?.value ?? "" };
            setForm(inicial);
            setOriginal(inicial);
            setContificoId(null);
        }
    }, [show, procedimiento, entidadActual]);

    const actualizar = (campo, valor) => setForm((previo) => ({ ...previo, [campo]: valor }));
    const marcarTocado = (campo) => setTocado((previo) => ({ ...previo, [campo]: true }));

    /* -- búsqueda en Contífico -------------------------------------------- */
    const buscarEnContifico = async () => {
        setBuscando(true);
        setResultado(null);
        try {
            const respuesta = await getCodeContifico(form.codigo.trim());

            // `getCodeContifico` devuelve `null` cuando axios lanza, y la
            // respuesta completa de axios cuando no. Se comprueban las dos
            // cosas explícitamente en vez de dejar que un TypeError caiga al
            // catch y se disfrace de "no encontrado", que es lo que hacía antes.
            if (!respuesta) {
                setResultado(RESULTADO.error());
                return;
            }
            if (respuesta.status === 403) {
                // El proxy exige `billing.view`, no `procedimientosprecios.*`
                // (billing.contifico.routes.ts). Un rol que administre
                // tarifarios sin acceso a facturación recibe 403, y eso NO es
                // "el producto no existe".
                setResultado(RESULTADO.sinPermiso());
                return;
            }
            const lista = Array.isArray(respuesta.data) ? respuesta.data : [];
            if (respuesta.status !== 200 || lista.length === 0) {
                setResultado(RESULTADO.noEncontrado());
                return;
            }

            const producto = lista[0];
            // Los nombres de campo de Contífico se leen con varias
            // alternativas y NUNCA se dan por hechos: si el producto no trae
            // nombre o precio, se dice que se encontró y no se rellena nada.
            // Inventar un valor aquí acabaría en una factura.
            const nombre = producto.nombre || producto.descripcion || null;
            const precioBruto = producto.pvp1 ?? producto.precio ?? producto.pvp ?? null;
            const precio = Number.isFinite(Number(precioBruto)) && precioBruto !== null
                ? Number(precioBruto)
                : null;

            setContificoId(producto.id || null);
            setResultado(RESULTADO.encontrado(nombre, precio, producto.id));

            // Sólo se rellena lo que está VACÍO: lo que el usuario ya escribió
            // manda sobre lo que diga Contífico.
            setForm((previo) => ({
                ...previo,
                descripcion: previo.descripcion.trim() === "" && nombre ? nombre : previo.descripcion,
                precio:
                    String(previo.precio).trim() === "" && precio !== null ? precio : previo.precio,
            }));
        } catch (error) {
            setResultado(RESULTADO.error());
        } finally {
            setBuscando(false);
        }
    };

    /* -- validación -------------------------------------------------------- */
    const errores = useMemo(() => {
        const e = {};
        if (form.codigo.trim() === "") e.codigo = "El código es obligatorio.";
        if (form.descripcion.trim() === "") e.descripcion = "La descripción es obligatoria.";
        if (!form.categoria_id) e.categoria_id = "Elige una categoría.";
        if (!form.seguro_id) e.seguro_id = "Elige la entidad del tarifario.";
        const precio = Number(form.precio);
        if (String(form.precio).trim() === "") e.precio = "El precio es obligatorio.";
        else if (!Number.isFinite(precio) || precio <= 0) e.precio = "El precio debe ser mayor que cero.";
        return e;
    }, [form]);

    const formularioValido = Object.keys(errores).length === 0;
    const cambiados = useMemo(() => camposCambiados(original, form), [original, form]);
    const hayCambios = cambiados.length > 0;
    const cambioDePrecio = useMemo(
        () => (esEdicion ? variacionPrecio(original.precio, form.precio) : null),
        [esEdicion, original.precio, form.precio]
    );

    // Al crear basta con que el formulario esté completo; al editar hace falta
    // además que algo haya cambiado -- guardar lo mismo que ya está guardado
    // no es una acción, y el botón lo dice en vez de fingir que funcionó.
    const puedeGuardar = formularioValido && (!esEdicion || hayCambios);
    const motivoBloqueo = !formularioValido
        ? "Faltan campos obligatorios."
        : esEdicion && !hayCambios
        ? "No has cambiado nada todavía."
        : undefined;

    /* -- guardar ----------------------------------------------------------- */
    const guardar = async () => {
        // Al pulsar con el formulario incompleto se marcan TODOS los campos
        // como tocados, para que los errores salgan a la vez y no de uno en
        // uno según se van visitando.
        if (!puedeGuardar) {
            setTocado({ codigo: true, descripcion: true, categoria_id: true, seguro_id: true, precio: true });
            return;
        }
        setGuardando(true);
        const payload = construirPayload(form, {
            contificoId,
            descuento: procedimiento?.descuento,
            idPrecio: procedimiento?.idprocedimiento_company_precios,
            estado: procedimiento?.ExamenTipo?.estado,
        });
        try {
            const respuesta = esEdicion
                ? await ProcedimientoActualizar(payload)
                : await ProcedimientoNuevo(payload);
            if (!respuesta?.success) throw new Error(respuesta?.message || "No se pudo guardar.");
            mostrar({ tipo: "exito", texto: respuesta.message });
            onHide();
            successRefresh?.();
        } catch (error) {
            mostrar({ tipo: "error", texto: error.message });
        } finally {
            setGuardando(false);
        }
    };

    /* -- eliminar (dentro de este mismo modal) ----------------------------- */
    const eliminar = async () => {
        setBorrando(true);
        const respuesta = await EliminarProcedimientoPrecio(
            procedimiento.idprocedimiento_company_precios
        );
        setBorrando(false);
        if (respuesta?.success) {
            mostrar({ tipo: "exito", texto: respuesta.message });
            onHide();
            successRefresh?.();
        } else {
            mostrar({
                tipo: "error",
                texto: respuesta?.message || "No se pudo eliminar el procedimiento.",
            });
        }
    };

    const copiarId = async () => {
        try {
            await navigator.clipboard.writeText(contificoId);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        } catch (error) {
            mostrar({ tipo: "error", texto: "El navegador no dejó copiar el ID." });
        }
    };

    const entidadDelForm = listaEntidades.find(
        (e) => String(e.value) === String(form.seguro_id)
    );
    const nombreEntidad = entidadDelForm?.label || entidadActual?.label || "";

    /* ------------------------------------------------------------------ */
    /* PASO "eliminar": el MISMO modal, otro contenido. Nunca uno encima de
       otro (Trampa 5). El botón "Atrás" devuelve al formulario con los
       cambios intactos. */
    if (paso === "eliminar") {
        return (
            <Modal
                abierto={show}
                titulo="Eliminar procedimiento"
                ancho="formulario"
                destructivo
                onCerrar={onHide}
                nota={
                    <Boton variante="ghost" tamano="sm" icono={IconoChevronIzquierda} onClick={() => setPaso("formulario")}>
                        Atrás
                    </Boton>
                }
                secundario={{ label: "Cancelar", onClick: () => setPaso("formulario") }}
                primario={{ label: "Eliminar", cargando: borrando, onClick: eliminar }}
            >
                <p>
                    Vas a eliminar <b>{form.codigo}</b> — {tituloClinico(form.descripcion)} del tarifario
                    de <b>{nombreEntidad}</b>. Esta acción no se puede deshacer.
                </p>
                {/* Lo que el backend SÍ garantiza: sólo se quita la fila de
                    precio de esta entidad (borrado lógico, `is_activo=false`),
                    el examen del catálogo no se toca. Lo que NO puede decir:
                    si ya se usó en facturas -- no existe esa comprobación, y
                    afirmarlo sería inventárselo. */}
                <p className="tar-nota">
                    Se elimina el precio de esta entidad. El procedimiento sigue existiendo para las
                    demás, y las facturas ya emitidas no cambian.
                </p>
            </Modal>
        );
    }

    return (
        <Modal
            abierto={show}
            titulo={esEdicion ? "Editar procedimiento" : "Nuevo procedimiento"}
            ancho="formulario"
            hayCambiosSinGuardar={hayCambios}
            onCerrar={onHide}
            nota={
                esEdicion && puedeEliminar ? (
                    <button
                        type="button"
                        className="tar-modal__eliminar"
                        onClick={() => setPaso("eliminar")}
                        data-testid="tarifario-modal-eliminar"
                    >
                        <IconoEliminar size={15} />
                        Eliminar
                    </button>
                ) : undefined
            }
            secundario={{ label: "Cancelar" }}
            primario={{
                label: esEdicion ? "Guardar cambios" : "Crear procedimiento",
                cargando: guardando,
                disabled: !puedeGuardar,
                onClick: guardar,
            }}
        >
            {/* Subtítulo: DE QUÉ TARIFARIO se trata. Sin esto, el modal era
                idéntico para las 19 aseguradoras. */}
            <div className="tar-modal__contexto" data-testid="tarifario-modal-contexto">
                {esEdicion && form.codigo && <span className="tar-modal__codigo">{form.codigo}</span>}
                <span>
                    {esEdicion ? "Tarifario de " : "Se agregará al tarifario de "}
                    <strong>{nombreEntidad || "—"}</strong>
                </span>
            </div>

            {/* FASE 4 · franja de sincronización, sólo al editar. */}
            {esEdicion &&
                (estaSincronizado(contificoId) ? (
                    <div className="tar-sync tar-sync--ok" data-testid="tarifario-sync">
                        <IconoCheck size={16} />
                        <span className="tar-sync__texto">Sincronizado con Contífico</span>
                        <code className="tar-sync__id" title={contificoId}>
                            {contificoId}
                        </code>
                        <button
                            type="button"
                            className="tar-sync__copiar"
                            onClick={copiarId}
                            title="Copiar el ID de Contífico"
                            aria-label="Copiar el ID de Contífico"
                            data-testid="tarifario-copiar-id"
                        >
                            {copiado ? <IconoCheck size={15} /> : <IconoCopiar size={15} />}
                        </button>
                    </div>
                ) : (
                    <div className="tar-sync tar-sync--pendiente" data-testid="tarifario-sync">
                        <IconoAlerta size={16} />
                        <span className="tar-sync__texto">Sin sincronizar con Contífico</span>
                        <Boton
                            tamano="sm"
                            variante="ghost"
                            icono={IconoBuscar}
                            cargando={buscando}
                            onClick={buscarEnContifico}
                            disabled={!form.codigo.trim()}
                        >
                            Buscar y enlazar
                        </Boton>
                    </div>
                ))}

            {/* FASE 3 · código + búsqueda. Al editar el código no se toca
                (así era antes y así sigue: cambiarlo reidentificaría el examen
                del catálogo), por eso este bloque sólo aparece al crear. */}
            {!esEdicion && (
                <div className="tar-modal__codigo-bloque">
                    <Campo
                        etiqueta="Código"
                        obligatorio
                        error={tocado.codigo ? errores.codigo : undefined}
                        // Ayuda FIJA, no placeholder: las instrucciones se
                        // necesitan mientras se escribe, que es justo cuando un
                        // placeholder desaparece.
                        ayuda="Si el procedimiento ya existe en Contífico, búscalo por su código y traemos la descripción y el precio. Si no existe, escribe los datos a mano."
                        htmlFor="tar-codigo"
                    >
                        <div className="tar-modal__codigo-fila">
                            <span className="kc__caja">
                                <input
                                    id="tar-codigo"
                                    type="text"
                                    className="kc__control tar-mono"
                                    value={form.codigo}
                                    onChange={(e) => actualizar("codigo", e.target.value)}
                                    onBlur={() => marcarTocado("codigo")}
                                    data-testid="tarifario-campo-codigo"
                                />
                            </span>
                            <Boton
                                tamano="lg"
                                variante="ghost"
                                icono={IconoBuscar}
                                cargando={buscando}
                                disabled={!form.codigo.trim()}
                                onClick={buscarEnContifico}
                                data-testid="tarifario-buscar-contifico"
                            >
                                Buscar en Contífico
                            </Boton>
                        </div>
                    </Campo>
                </div>
            )}

            {/* El resultado de la búsqueda se muestra SIEMPRE. Antes no había
                ninguna señal de si había encontrado algo. */}
            {resultado && (
                <div className="tar-modal__resultado" data-testid="tarifario-resultado-contifico">
                    {resultado.tipo === "encontrado" && (
                        <Aviso tipo="exito">
                            {resultado.nombre ? (
                                <>
                                    Encontrado en Contífico: <strong>{tituloClinico(resultado.nombre)}</strong>.
                                    Al guardar, quedará enlazado automáticamente.
                                </>
                            ) : (
                                <>Encontrado en Contífico. Al guardar, quedará enlazado automáticamente.</>
                            )}
                        </Aviso>
                    )}
                    {resultado.tipo === "no-encontrado" && (
                        <Aviso tipo="info">No está en Contífico. Se creará sólo en SmartMedic.</Aviso>
                    )}
                    {resultado.tipo === "sin-permiso" && (
                        <Aviso tipo="aviso">
                            Tu perfil no tiene acceso a la búsqueda en Contífico. Puedes guardar el
                            procedimiento igualmente; quedará sin sincronizar.
                        </Aviso>
                    )}
                    {resultado.tipo === "error" && (
                        <Aviso
                            tipo="aviso"
                            accion={
                                <Boton tamano="sm" onClick={buscarEnContifico} cargando={buscando}>
                                    Reintentar
                                </Boton>
                            }
                        >
                            No se pudo conectar con Contífico.
                        </Aviso>
                    )}
                </div>
            )}

            {/* Una línea, no un `<textarea>`: es un nombre, no un párrafo. */}
            <CampoTexto
                etiqueta="Descripción"
                obligatorio
                value={form.descripcion}
                onChange={(e) => actualizar("descripcion", e.target.value)}
                onBlur={() => marcarTocado("descripcion")}
                error={tocado.descripcion ? errores.descripcion : undefined}
                ayuda="Es el nombre que verá quien factura. Se escribe normal, no en mayúsculas."
                data-testid="tarifario-campo-descripcion"
            />

            <div className="tar-modal__dos">
                <CampoSelect
                    etiqueta="Categoría"
                    obligatorio
                    value={form.categoria_id}
                    onChange={(e) => actualizar("categoria_id", e.target.value)}
                    onBlur={() => marcarTocado("categoria_id")}
                    error={tocado.categoria_id ? errores.categoria_id : undefined}
                    placeholder="Seleccione"
                    opciones={categorias}
                    data-testid="tarifario-campo-categoria"
                />
                <CampoSelect
                    etiqueta="Tipo"
                    value={form.tipo}
                    onChange={(e) => actualizar("tipo", e.target.value)}
                    placeholder="Sin especificar"
                    opciones={tipos}
                    data-testid="tarifario-campo-tipo"
                />
            </div>

            <div className="tar-modal__dos">
                <Campo
                    etiqueta="Entidad / seguro"
                    obligatorio
                    error={tocado.seguro_id ? errores.seguro_id : undefined}
                    ayuda={esEdicion ? undefined : "Viene del tarifario que estás editando."}
                    htmlFor="tar-entidad-campo"
                >
                    <span className="kc__caja tar-modal__entidad">
                        {nombreEntidad && <Avatar nombre={nombreEntidad} tamano={22} />}
                        <select
                            id="tar-entidad-campo"
                            className="kc__control"
                            value={form.seguro_id}
                            onChange={(e) => actualizar("seguro_id", e.target.value)}
                            onBlur={() => marcarTocado("seguro_id")}
                            data-testid="tarifario-campo-entidad"
                        >
                            <option value="">Seleccione</option>
                            {listaEntidades.map((e) => (
                                <option key={e.value} value={e.value}>
                                    {e.label}
                                </option>
                            ))}
                        </select>
                    </span>
                </Campo>

                <Campo
                    etiqueta="Precio"
                    obligatorio
                    error={tocado.precio ? errores.precio : undefined}
                    ayuda={
                        !esEdicion && nombreEntidad
                            ? `Precio para ${nombreEntidad}. No afecta a otras entidades.`
                            : undefined
                    }
                    htmlFor="tar-precio"
                >
                    <span className={`kc__caja tar-modal__precio${cambioDePrecio ? " is-cambiado" : ""}`}>
                        <span className="tar-modal__precio-simbolo" aria-hidden="true">
                            $
                        </span>
                        <input
                            id="tar-precio"
                            type="number"
                            step="0.01"
                            min="0"
                            className="kc__control tar-mono tar-modal__precio-input"
                            value={form.precio}
                            onChange={(e) => actualizar("precio", e.target.value)}
                            onBlur={() => marcarTocado("precio")}
                            data-testid="tarifario-campo-precio"
                        />
                    </span>
                    {/* Sólo aparece cuando el valor cambió de verdad. */}
                    {cambioDePrecio && (
                        <div className="tar-modal__cambio" data-testid="tarifario-cambio-precio">
                            <span className="tar-modal__cambio-antes">
                                {formatearPrecio(cambioDePrecio.anterior)}
                            </span>
                            <IconoFlecha size={13} />
                            <span
                                className={`tar-modal__cambio-despues${
                                    cambioDePrecio.subio ? " is-sube" : " is-baja"
                                }`}
                            >
                                {formatearPrecio(cambioDePrecio.nuevo)}
                            </span>
                            {cambioDePrecio.porcentaje !== null && (
                                <span className="tar-modal__cambio-pct">
                                    · {formatearPorcentaje(cambioDePrecio.porcentaje)}
                                </span>
                            )}
                        </div>
                    )}
                </Campo>
            </div>

            {/* Los dos avisos de alcance. No son decorativos: dicen a qué
                afecta lo que se está a punto de guardar, y su contenido está
                verificado contra el backend (ver utils/tarifarios.js). */}
            {esEdicion && cambioDePrecio && (
                <Aviso tipo="aviso" className="tar-modal__alcance">
                    {ALCANCE_PRECIO}
                </Aviso>
            )}
            {esEdicion && tocaElCatalogo(cambiados) && (
                <Aviso tipo="aviso" className="tar-modal__alcance">
                    {ALCANCE_CATALOGO}
                </Aviso>
            )}

            {/* Por qué "Guardar cambios" está apagado, dicho donde se mira. */}
            {motivoBloqueo && (
                <p className="tar-modal__motivo" data-testid="tarifario-motivo-bloqueo">
                    {motivoBloqueo}
                </p>
            )}
        </Modal>
    );
};

export default ModalProcedimientoPrecio;
